/**
 * @Author: v_jppjpeng
 * @Date: 2022-9-16 15:49:04
 * @Explain: 数字类型 - 格式化
 */
import { onPattern, pattern } from '@/utils/regular';
import BigNumber from 'bignumber.js';
import { isInvalidValue } from '../common/dataJudgment';

// parseFloat
export const onParseFloat = (num: number, decimalPlaces = 2) =>
  Number.isNaN(num) ? 0 : parseFloat(String(num)).toFixed(decimalPlaces);

// 获取百分比
export const getPercentage = (num: number, decimalPlaces = 2) => {
  const newNum = Number.isNaN(num) || !Number.isFinite(num) ? 0 : num;
  return `${newNum >= 0 ? onParseFloat(newNum * 100, decimalPlaces) : '0.00'}%`;
};

// 获取百分比数字
export const getPercentageNum = (num: number, decimalPlaces = 2) => {
  const newNum = Number.isNaN(num) || !Number.isFinite(num) ? 0 : num;
  return newNum >= 0 ? +onParseFloat(newNum * 100, decimalPlaces) : 0;
};
/**
 * 保留有效位数
 * 只有数字小于0.01 才会启用
 * @precision 有效位数
 */

export const onReservedSignificant = (num: number, precision: number = 3) => {
  return num < 0.01 ? parseFloat(num.toPrecision(precision)) : num;
};
/**
 * 获取小数位数有多少位
 * 只有数字小于0.001 才会启用
 */
export const countDecimalPlaces = (num: number, decimalPlaces = 2) => {
  const numStr = num.toString(); // 将数字转换为字符串
  const match = numStr.match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/); // 使用正则表达式匹配小数点和指数部分
  if (!match) {
    return 0;
  }

  const _decimalPlaces = match[1] ? match[1].length : 0;
  const exponent = match[2] ? parseInt(match[2], 10) : 0;

  return num < 0.001 ? _decimalPlaces - exponent - 2 : decimalPlaces;
};

/**
 * 保留小数位数
 * @params value 待处理值
 * @params precision 保留小数位数
 * @params complement 整数位是否补零 true:不补零  false:补零
 */
export const onReservedDecimal = ({
  value = 0,
  precision = 2,
  complement = true,
}: {
  value: number;
  precision: number;
  complement?: boolean;
}) => {
  if (complement) {
    return parseFloat(value.toFixed(precision));
  } else {
    return value.toFixed(precision);
  }
};

/**
 * 数字千分位分割格式函数
 * @param value 需要转化的数值
 * @param options 配置选项
 * @returns 格式化后的字符串
 */
export function millimeterFormat(
  value: string | number,
  {
    round = true,
    isDecimal = false,
    decimalPlaces = 2,
    adaptiveFilling = false,
  }: {
    // 是否四舍五入保留指定位数（默认开启）
    round?: boolean;
    // 是否保留小数位（默认不开启）
    isDecimal?: boolean;
    // 小数位数（默认保留两位）
    decimalPlaces?: number;
    // 自适应填充，即是否去除尾部零（默认不开启）
    adaptiveFilling?: boolean;
  } = {},
): string {
  // 处理空值和NaN
  let sourceValue: string | number = Number(value);
  if (value === null || value === undefined || Number.isNaN(sourceValue)) {
    sourceValue = 0;
  }

  // 负数处理：存储符号前缀
  let before = '';
  // 处理负数：存储符号前缀，将数值转为正数处理后再添加负号
  if (compareNum(sourceValue, 0) === -1) {
    before = '-';
    sourceValue = -sourceValue;
  }

  // 四舍五入保留指定位数
  if (round) {
    sourceValue = (+sourceValue).toFixed(decimalPlaces);
  } else {
    // 保留指定位数不四舍五入
    sourceValue = formatNumberNotRounding({
      num: sourceValue,
      decimalPlaces,
      padZero: true,
    });
  }
  // 拆分数值为整数部分和小数部分
  const valueArr = sourceValue.toString().split('.');
  // 整数部分
  let integerNum = valueArr[0];
  // 小数部分
  let decimalNum = valueArr[1];
  // 存储结果
  let result = '';

  // 千分位分割（传入值小于1000不切割）
  if (compareNum(integerNum, 1000) === -1) {
    result = integerNum;
  } else {
    const list = [];
    while (integerNum.length > 3) {
      // 倒序切割
      list.unshift(integerNum.slice(-3));
      integerNum = integerNum.slice(0, -3);
    }
    // 处理剩余长度
    list.unshift(integerNum);
    result = list.join(',');
  }

  // 添加小数部分
  if (isDecimal) {
    if (adaptiveFilling) {
      // 自适应填充：去除尾部零
      decimalNum =
        parseFloat(`0.${decimalNum}`).toString().split('.')?.[1] || '';
      result = decimalNum ? `${result}.${decimalNum}` : result;
    } else {
      result = `${result}.${decimalNum}`;
    }
  }
  result = `${before}${result}`;

  return result;
}

// 字节转换相应大小
export function bytesToSize(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(`${Math.floor(Math.log(bytes) / Math.log(1024))}`, 10);
  return `${Math.round(bytes / 1024 ** i)}${sizes[i]}`;
}

// 获取字符串字节码长度
export function getStrBytes(str?: string) {
  if (isInvalidValue(str)) return 0;
  if (typeof str !== 'string') {
    return 0;
  }
  let total = 0;
  let charCode;
  let i;
  let len;
  for (i = 0, len = str.length; i < len; i += 1) {
    charCode = str.charCodeAt(i);
    if (charCode <= 0x007f) {
      total += 1; // 字符代码在000000 – 00007F之间的，用一个字节编码
    } else if (charCode <= 0x07ff) {
      total += 2; // 000080 – 0007FF之间的字符用两个字节
    } else if (charCode <= 0xffff) {
      total += 3; // 000800 – 00D7FF 和 00E000 – 00FFFF之间的用三个字节，注: Unicode在范围 D800-DFFF 中不存在任何字符
    } else {
      total += 4; // 010000 – 10FFFF之间的用4个字节
    }
  }
  return total;
}

// 获取区间随机数
export function getRandomNumberByRange(start: number, end: number) {
  return Math.floor(Math.random() * (end - start) + start);
}

// 获取n位随机数
export function getRandomNum(num: number) {
  let value = '';
  for (let i = 0; i < num; i++) {
    value += Math.floor(Math.random() * 10);
  }
  return value;
}

// 非四舍五入格式化数字、保留指定位数、可控制是否补零
export const formatNumberNotRounding = ({
  num,
  decimalPlaces,
  padZero = false,
}: {
  // 待格式化数值
  num: number | string;
  // 保留小数位数
  decimalPlaces?: number;
  // 小数位数不足是否补零
  padZero?: boolean;
}): string => {
  // 将数字转换为字符串
  let numStr = num.toString();

  if (!decimalPlaces || !Number.isInteger(decimalPlaces)) {
    return numStr;
  }

  // 检查是否包含小数点
  if (!numStr.includes('.')) {
    numStr += '.';
  }

  // 分离整数部分和小数部分
  const [integerPart, decimalPart] = numStr.split('.');

  // 截取小数部分到指定的位数
  let formattedDecimal = decimalPart.slice(0, decimalPlaces);

  // 如果需要补零，确保小数部分长度等于指定的小数位数
  if (padZero && formattedDecimal.length < decimalPlaces) {
    formattedDecimal = formattedDecimal.padEnd(decimalPlaces, '0');
  }

  let resNumber = `${integerPart}.${formattedDecimal}`;

  // 返回格式化后的数字
  return resNumber;
};

/**
 * 格式化数字显示
 * @num 数字
 * @digits 小数点后的位数
 **/
export function formatDigitalDisplay(
  num: number,
  digits = 0,
  fixedShow = false,
  rounding = true, // 是否四舍五入
) {
  const si = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let i;
  for (i = si.length - 1; i > 0; i--) {
    if (num >= si[i].value) {
      break;
    }
  }

  if (fixedShow) {
    let resNum = dividedBy(num, si[i].value);
    if (String(resNum) && Number.isInteger(resNum)) {
      return resNum + si[i].symbol;
    }
    let newValue = rounding
      ? resNum.toFixed(digits)
      : formatNumberNotRounding({
          num: resNum,
          decimalPlaces: digits,
          padZero: true,
        });
    return newValue + si[i].symbol;
  }

  let newValue = rounding
    ? dividedBy(num, si[i].value).toFixed(digits)
    : formatNumberNotRounding({
        num: dividedBy(num, si[i].value),
        decimalPlaces: digits,
        padZero: true,
      });
  return newValue.replace(rx, '$1') + si[i].symbol;
}

/** 加法 */
export const addBy = (a: number, b: number) => {
  const x = new BigNumber(a);
  const y = x.plus(b).toNumber();
  return y;
};

/** 减法 */
export const minusBy = (a: number, b: number) => {
  const x = new BigNumber(a);
  const y = x.minus(b).toNumber();
  return y;
};

/** 乘法 */
export const multipliedBy = (a: number, b: number) => {
  const x = new BigNumber(a);
  const y = x.times(b).toNumber();
  return y;
};

/** 除法 - a: 分子  b: 分母 */
export const dividedBy = (a: number, b: number) => {
  const x = new BigNumber(a);
  const y = x.div(b).toNumber();
  return y;
};

/** 取模/取余 */
export const moduloBy = (a: number, b: number) => {
  const x = new BigNumber(a);
  const y = x.modulo(b).toNumber();
  return y;
};

/** 加法 - string */
export const addByString = (a: string, b: string) => {
  const x = new BigNumber(a);
  const y = x.plus(b).toFormat().replaceAll(',', '');
  return y;
};

/** 加法 - string */
export const minusByString = (a: string, b: string) => {
  const x = new BigNumber(a);
  const y = x.minus(b).toFormat().replaceAll(',', '');
  return y;
};

/**
 *
 * 比较大小
 * 返回值： 1-> 大于  0 -> 等于  -1 ->小于
 */
export const compareNum = (a: number | string, b: number | string) => {
  let numberA = new BigNumber(a);
  return numberA.comparedTo(b);
};

interface percentageTypes {
  // 保留几位小数
  num?: number;
  // 分子
  molecule: number;
  // 分母
  denominator: number;
  // 是否使用四舍五入规则
  useRound?: boolean;
  // 是否补零 true:不补零  false:补零
  complement?: boolean;
  // 打开保底操作 - 配合后台处理百分比超过100%的展示100%
  openGuarantee?: boolean;
  // 特殊处理控制0% 和 100% 即使开启补零行为末尾也不会显示补零: 默认不开启
  openSpacelComplement?: boolean;
}
// 计算百分比
export const percentage = (data: percentageTypes) => {
  const {
    num = 2,
    molecule,
    denominator,
    useRound = true,
    complement = true,
    openGuarantee = false,
    openSpacelComplement = false,
  } = data;
  if (!denominator) {
    const after = Array.from({ length: num }, () => 0).join('');
    return !complement && num ? `0.${after}` : 0;
  }

  const dividedRes = dividedBy(molecule, denominator);
  let percent = multipliedBy(dividedRes, 100);

  // JS不进行四舍五入
  if (!useRound) {
    let numStr = percent.toString();
    const index = numStr.indexOf('.');
    if (index !== -1) {
      numStr = numStr.substring(0, num + index + 1);
    } else {
      numStr = numStr.substring(0);
    }
    percent = +numStr;
  }

  // 打开保底操作: 配合后台处理百分比比值超过100的展示100
  if (compareNum(percent, 100) === 1 && openGuarantee) {
    percent = 100;
  }

  if (complement) {
    const isZero = !compareNum(percent, 0);
    const isOneHundred = !compareNum(percent, 100);
    // 特殊处理控制0% 和 100% 即使开启补零行为末尾也不会显示补零
    if (openSpacelComplement && (isZero || isOneHundred)) {
      return percent;
    }
    return parseFloat(percent.toFixed(num));
  }

  return percent.toFixed(num);
};

// 数字符合要求范围值
const maxMin = (params: { value: string; type?: string }) => {
  const { value, type } = params;
  let obj = { max: undefined, min: undefined, num: 0 };
  if (type === 'int64') {
    // (-2^63) (2^63-1)
    return Object.assign(obj, {
      // max: 9223372036854775807,
      // min: -9223372036854775808,
      max: new BigNumber('9223372036854775807'),
      min: new BigNumber('-9223372036854775808'),
    });
  }
  return obj;
};

// 验证number是否在指定范围
export const verifiNumRange = (params: { value: string; type?: string }) => {
  const { value, type } = params;
  // 获取数字符合要求范围值
  let maxMinObj: any = maxMin(params);
  // 格式化被验证值
  let bigV: any = new BigNumber(value);

  // 验证Int 类型不可包含小数
  // onPattern(value, pattern.numberConcatNegative); // 验证是数字 (包含正负数，不包含小数)
  // onPattern(value, pattern.eInteger) // 验证是带有科学计数法的整数
  // maxMinObj.min.comparedTo(bigV) < 1 => // 判断是否在最小值之内(包含最小值)
  // maxMinObj.max.comparedTo(bigV) > -1 => // 判断是否在最大值之内(包含最大值)
  if (
    type === 'int64' &&
    (onPattern(value, pattern.numberConcatNegative) ||
      onPattern(value, pattern.eInteger)) &&
    maxMinObj.max.comparedTo(bigV) > -1 &&
    maxMinObj.min.comparedTo(bigV) < 1
  ) {
    return true;
  }
  let str = `请输入数字，范围在(${maxMinObj?.min
    ?.toFormat()
    ?.replaceAll(',', '')}~${maxMinObj?.max?.toFormat()?.replaceAll(',', '')})`;
  console.log(str);
  return false;
};

// 将阿拉伯数字（1、2、3）转换为中文数字（一、二、三）的方法
export const convertToChineseNumber = (num: string) => {
  // 判断是否是数值
  let isNum = onPattern(num, pattern.isNumberConcatNegativeFloat);
  if (!isNum) return num;

  let prefix = '';
  let newNum = num;
  // 处理负数情况
  if (num[0] === '-') {
    prefix = '负';
    newNum = num.replaceAll('-', '');
  }

  let AA = new Array(
    '零',
    '一',
    '二',
    '三',
    '四',
    '五',
    '六',
    '七',
    '八',
    '九',
    '十',
  );
  let BB = new Array('', '十', '百', '仟', '萬', '億', '点', '');
  let a: string[] = ('' + newNum).replace(/(^0*)/g, '').split('.'),
    k = 0,
    re = '';
  for (let i = a[0].length - 1; i >= 0; i--) {
    switch (k) {
      case 0:
        re = BB[7] + re;
        break;
      case 4:
        if (!new RegExp('0{4}//d{' + (a[0].length - i - 1) + '}$').test(a[0]))
          re = BB[4] + re;
        break;
      case 8:
        re = BB[5] + re;
        BB[7] = BB[5];
        k = 0;
        break;
    }
    if (k % 4 == 2 && a[0].charAt(i + 2) != '0' && a[0].charAt(i + 1) == '0')
      re = AA[0] + re;
    if (a[0].charAt(i) != '0') re = AA[+a[0].charAt(i)] + BB[k % 4] + re;
    k++;
  }
  if (a.length > 1) {
    // 加上小数部分(如果有小数部分)
    re += BB[6];
    for (let i = 0; i < a[1].length; i++) re += AA[+a[1].charAt(i)];
  }
  if (re == '一十') re = '十';
  if (re.match(/^一/) && re.length == 3) re = re.replace('一', '');

  return `${prefix}${re}`;
};

/**
 * 总数平均分
 * @param groupCount 平分数
 * @param total 总数
 * @returns Array
 * 例子：
 * 假设 100 平分 3等分 结果[33.33, 33.33, 33.34]
 */
export function onDivideEqually(groupCount: number, total = 100) {
  const ratio = total / groupCount;
  const groups = [];

  for (let i = 0; i < groupCount - 1; i++) {
    const groupValue = parseFloat(ratio.toFixed(2));
    groups.push(groupValue);
  }

  // 计算最后一组的值，使其与之前所有组的和等于 total
  const lastGroupValue = total - groups.reduce((sum, value) => sum + value, 0);
  groups.push(parseFloat(lastGroupValue.toFixed(2)));

  return groups;
}
